"use client";

import { BUTTON_SMALL, TABLE, TD, TH } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import type { DayNumber } from "@/lib/tool-kit/study/dates";
import { parseIsoDay } from "@/lib/tool-kit/study/dates";
import type { LogRow } from "@/lib/tool-kit/study/writing-goal";

/*
 * The words-per-day calculator's progress log: one row per check-in with the change
 * since the one before, and a remove button per row. Every word comes in through
 * `labels`, from the widget's strings.
 */

export interface ProgressLogLabels {
  date: string;
  total: string;
  change: string;
  remove: string;
  /** "Remove the entry for {date}" — each row's button label. */
  removeEntry: string;
}

export function ProgressLogTable({
  rows,
  labels,
  formatDay,
  fmt,
  onRemove,
}: {
  rows: readonly LogRow[];
  labels: ProgressLogLabels;
  formatDay: (day: DayNumber) => string;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
  onRemove: (date: string) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            {[labels.date, labels.total, labels.change].map((label) => (
              <th className={`${TH} whitespace-nowrap`} key={label} scope="col">
                {label}
              </th>
            ))}
            <th className={TH} scope="col">
              <span className="sr-only">{labels.remove}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const date = formatDay(parseIsoDay(row.date) ?? 0);
            return (
              <tr key={row.date}>
                <td className={`${TD} whitespace-nowrap`}>{date}</td>
                <td className={TD}>{fmt(row.total)}</td>
                <td className={TD}>
                  {row.change === null
                    ? "—"
                    : fmt(row.change, { signDisplay: "exceptZero" })}
                </td>
                <td className={TD}>
                  <button
                    aria-label={t(labels.removeEntry, { date })}
                    className={BUTTON_SMALL}
                    onClick={() => onRemove(row.date)}
                    type="button"
                  >
                    ×
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
